import React, {useCallback, useMemo, useState} from 'react';

import {EMAIL_SETTINGS} from 'src/constants';
import {EmailType as EmailTypes} from 'src/openapi/admin/models/EmailType';
import {useGetAccountInfo, useGetAccountTeam} from 'src/use-cases';
import {useGetAccountSSOSettings} from 'src/use-cases/account/useGetAccountSSOSettings';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';


import {SelectEmailTypeBlock} from './blocks/select-email-type';
import {SelectTeamBlock} from './blocks/select-team-block';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {EmailType} from 'src/openapi/admin/models/EmailType';
import type {WelcomeEmailSendRequest} from 'src/openapi/admin/models/WelcomeEmailSendRequest';

type Props = {
    userIds: number[],
    accountId: number,
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<WelcomeEmailSendRequest>,
};


export const SendWelcomeEmailModal = ({userIds, onCloseModal, processAction, accountId}: Props) => {
    const [emailType, setEmailType] = useState<EmailType | null>(null);
    const [selectedTeamMembers, selectTeamMembers] = useState<AccountManagerInfo[]>([]);
    const [currentStep, setCurrentStep] = useState(0);

    const {
        data: teamMembers,
        isLoading: isTeamMembersLoading,
        loadingError: teamMembersLoadingError,
    } = useGetAccountTeam({accountId});

    const {
        data: accountInfo,
        isLoading: isAccountLoading,
        loadingError: accountLoadingError,
    } = useGetAccountInfo({accountId});

    const {
        data: ssoSettings,
        isLoading: isSsoLoading,
        loadingError: ssoLoadingError,
    } = useGetAccountSSOSettings({accountId});

    const sendEmail = useCallback(() => {
        const managerIds = selectedTeamMembers.map(({id}) => id);

        if (emailType) {
            processAction({userIds, managerIds, emailType});
        }
    }, [emailType, userIds, processAction, selectedTeamMembers]);

    const isLoading = useMemo(() => [
        isSsoLoading,
        isAccountLoading,
        isTeamMembersLoading,
    ].some(Boolean), [isSsoLoading, isAccountLoading, isTeamMembersLoading]);

    const loadingError = useMemo(() => [
        ssoLoadingError,
        accountLoadingError,
        teamMembersLoadingError,
    ].find(Boolean) || null, [ssoLoadingError, accountLoadingError, teamMembersLoadingError]);

    const sendDisabled = useMemo(() => {
        const teamMemberFreeEmail = !!emailType && ![
            EmailTypes.WELCOME_EMAIL,
            EmailTypes.STARTUP_WELCOME_EMAIL,
            EmailTypes.NEW_CLIENT_WELCOME_EMAIL,
            EmailTypes.SMB_WELCOME_EMAIL,
            EmailTypes.ORIGIN_WELCOME_EMAIL,
            // Falls back to Support contacts when no account team member is selected
            EmailTypes.LLM_ONLY_WELCOME_EMAIL,
        ].includes(emailType);
        return teamMemberFreeEmail && !selectedTeamMembers.length;
    }, [emailType, selectedTeamMembers]);

    const steps = useMemo(() => [
        {
            caption: 'Select Email Type',
            content: (
                <SelectEmailTypeBlock
                    isLoading={isLoading}
                    loadingError={loadingError}
                    teamMembers={teamMembers}
                    accountInfo={accountInfo}
                    ssoSettings={ssoSettings}
                    emailType={emailType}
                    setEmailType={setEmailType}
                />
            ),
            footer: (
                <DialogFooter>
                    <Button disabled={!emailType} variant="Type.PRIMARY" onClick={() => setCurrentStep(1)}>
                        Next
                    </Button>
                </DialogFooter>
            ),
        },
        {
            caption: EMAIL_SETTINGS[emailType || EmailTypes.WELCOME_EMAIL].label,
            content: (
                <SelectTeamBlock
                    emailType={emailType || EmailTypes.WELCOME_EMAIL}
                    teamMembers={teamMembers}
                    selectedTeamMembers={selectedTeamMembers}
                    selectTeamMembers={selectTeamMembers}
                />
            ),
            footer: (
                <DialogFooter>
                    <Group>
                        <Button disabled={sendDisabled} variant="Type.PRIMARY" onClick={sendEmail}>
                            Send
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={() => setCurrentStep(0)}>
                            Back
                        </Button>
                        <Button variant="Type.TERTIARY" onClick={() => onCloseModal(false)}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            ),
        },
    ], [
        isLoading,
        loadingError,
        teamMembers,
        accountInfo,
        ssoSettings,
        emailType,
        selectedTeamMembers,
        sendDisabled,
        sendEmail,
        onCloseModal,
    ]);

    const {caption, content, footer} = steps[currentStep];

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {caption}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {content}
            </DialogMain>
            {footer}
        </>
    );
};
